import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';

import { THREAT_INDEX_COLOR } from 'enum/constants';

const ThreatIndexBar = ({ low, medium, high, markerValue, index }) => {
  const sections = [
    { label: 'LOW', range: [0, low], color: THREAT_INDEX_COLOR.LOW, markerColor: '#B6BBF5' },
    { label: 'MEDIUM', range: [low, medium], color: THREAT_INDEX_COLOR.MEDIUM, markerColor: '#F9E9CB' },
    { label: 'HIGH', range: [medium, high], color: THREAT_INDEX_COLOR.HIGH, markerColor: '#F9C39E' },
    { label: 'CRITICAL', range: [high, 10], color: THREAT_INDEX_COLOR.CRITICAL, markerColor: '#E0A9AB' },
  ];

  const stepCount = 11; // From 0 to 10 (inclusive)
  const stepWidth = 100 / (stepCount - 1); // Percentage width per step

  // Determine marker color based on its range
  const { label: markerLabel, markerColor } =
    sections.find((section) => markerValue >= section.range[0] && markerValue < section.range[1]) ||
    sections[sections.length - 1];

  return (
    <Box sx={{ display: 'flex', height: 100, alignItems: 'center' }}>
      <Box sx={{ backgroundColor: '#5764F5', borderRadius: '8px 0 0 8px', width: 12, height: 8 }} />
      <Box sx={{ textAlign: 'center', position: 'relative', flex: 1 }}>
        {/* Step Numbers & Sticks */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', position: 'relative', width: '100%' }}>
          {Array.from({ length: stepCount }, (_, i) => (
            <Box
              key={i}
              sx={{ position: 'absolute', left: `${i * stepWidth}%`, bottom: 0, transform: 'translateX(-50%)' }}
            >
              <Typography sx={{ fontSize: '12px' }}>{i}</Typography>
              {/* Step Stick */}
              <Box
                sx={{
                  width: '1px',
                  height: 8,
                  backgroundColor: '#D1D5D9',
                  margin: '2px auto 0',
                }}
              />
            </Box>
          ))}
        </Box>

        {/* Status Bar */}
        <Box sx={{ display: 'flex', width: '100%', height: 8, overflow: 'hidden' }}>
          {sections.map((section, index) => (
            <Box
              key={index}
              sx={{
                flex: section.range[1] - section.range[0],
                backgroundColor: section.color,
              }}
            />
          ))}
        </Box>
        {/* Marker */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: `${(markerValue / 10) * 100}%`,
            transform: 'translateX(-50%)',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              backgroundColor: markerColor,
              position: 'relative',
              clipPath: 'polygon(0% 100%, 100% 100%, 100% 40%, 50% 0%, 0% 40%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pt: 1,
              fontSize: 14,
              fontWeight: 'bold',
              boxShadow: 2,
            }}
            data-testid={`threat-index-${index}`}
          >
            {markerValue}
          </Box>

          <Typography sx={{ fontSize: 12, position: 'absolute', transform: 'translateX(-50%)', left: '50%', pt: 1 }}>
            {markerLabel}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ backgroundColor: '#C0383D', borderRadius: '0 8px 8px 0', width: 12, height: 8 }} />
    </Box>
  );
};

ThreatIndexBar.propTypes = {
  low: PropTypes.number,
  medium: PropTypes.number,
  high: PropTypes.number,
  markerValue: PropTypes.number,
  index: PropTypes.string,
};

export default ThreatIndexBar;
